// God → Overview: platform volume, Auto Pay snapshot, attention items, health and versions.
// Reference page for the kit — keep other pages structured the same way.
import { useCallback, useEffect, useState } from "react";
import {
  Activity, AlertTriangle, ArrowDownToLine, Banknote, Database, Link2, Server, Smartphone, Store, Users, Zap,
} from "lucide-react";
import { god } from "../godApi";
import {
  Callout, Cell, Empty, Grid, Health, Kpi, Page, Pill, Rows, Section, Skeleton, Table, ago, inr, inrCompact, useNav,
} from "../kit";

const sumBy = (x, keys) => keys.reduce((a, k) => ({ c: a.c + (x?.[k]?.count || 0), v: a.v + (x?.[k]?.amount || 0) }), { c: 0, v: 0 });
const uptime = (s) => (s >= 86400 ? `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`);

export default function Overview() {
  const { go } = useNav();
  const [ov, setOv] = useState(null);
  const [st, setSt] = useState(null);
  const [vr, setVr] = useState(null);
  const [gw, setGw] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, b, c, d] = await Promise.all([god.overview(), god.status(), god.versions(), god.gwOverview().catch(() => null)]);
      setOv(a); setSt(b); setVr(c); setGw(d); setErr("");
    } catch (e) {
      setErr(e.message);
    }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  if (!ov || !st) return <Page>{err ? <Callout tone="red" title="Couldn't load the overview">{err}</Callout> : <Section><Skeleton rows={6} /></Section>}</Page>;

  const intents = ov.intents;
  const totalIntents = intents.awaiting + intents.settled + intents.expired || 1;
  const pinToday = sumBy(gw?.payins_today, ["success", "dispute"]);
  const poToday = sumBy(gw?.payouts_today, ["success"]);
  const gap = gw?.upstream_payout_balance != null ? gw.upstream_payout_balance - gw.merchant_balances_total : null;
  const released = vr?.mobile_released;
  const fleet = vr?.mobile_in_field || [];
  const outdated = fleet.filter((m) => m.version !== released).reduce((a, m) => a + m.devices, 0);
  const collections = Object.entries(st.database.collections || {}).sort((a, b) => b[1] - a[1]);

  const attention = [];
  if (!st.database.connected) attention.push({ tone: "red", title: "Database is unreachable", body: "API requests that touch the database will fail until it reconnects." });
  if (gw?.payouts_in_review > 0) attention.push({ tone: "red", title: `${gw.payouts_in_review} payout(s) held for review`, body: "The bank network didn't give a clear answer. Check them before money moves again.", to: "gateway/payouts" });
  if (ov.unmatched_credits > 0) attention.push({ tone: "amber", title: `${ov.unmatched_credits} unmatched bank credit(s)`, body: "Money arrived that no open payment matched.", to: "notifications" });
  if (gw?.deliveries_failing > 0) attention.push({ tone: "amber", title: `${gw.deliveries_failing} merchant callback(s) failing`, body: "Merchants aren't acknowledging payout/payin callbacks.", to: "gateway/callbacks/merchants" });
  if (gw && !gw.settings?.configured) attention.push({ tone: "blue", title: "ProtonPay isn't configured", body: "Auto Pay and payouts stay off until credentials are saved.", to: "gateway/account" });

  return (
    <Page>
      {err && <Callout tone="amber" title="Showing the last good data">{err}</Callout>}

      <Grid cols={4}>
        <Kpi hero icon={Activity} label="Collected today" value={inr(ov.today.sum)} sub={`${ov.today.count.toLocaleString("en-IN")} payments`} />
        <Kpi icon={Banknote} label="This month" value={inrCompact(ov.month.sum)} sub={`${ov.month.count.toLocaleString("en-IN")} payments`} />
        <Kpi icon={Database} label="Lifetime" value={inrCompact(ov.processed.sum)} sub={`${ov.processed.count.toLocaleString("en-IN")} payments`} />
        <Kpi icon={Store} label="Merchants" value={ov.merchants} sub={ov.suspended_merchants ? `${ov.suspended_merchants} suspended` : "none suspended"} onClick={() => go("merchants")} />
      </Grid>

      {attention.length > 0 && (
        <Section title="Needs attention" icon={AlertTriangle} pad>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {attention.map((a) => (
              <Callout key={a.title} tone={a.tone} title={a.title}
                action={a.to && <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => go(a.to)}>Open</button>}>
                {a.body}
              </Callout>
            ))}
          </div>
        </Section>
      )}

      <div className="k-split">
        <Section title="Auto Pay today" sub="ProtonPay gateway across all merchants" icon={Zap}
          actions={<button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => go("gateway")}>Open gateway</button>}>
          {!gw ? <Empty title="Gateway data unavailable" /> : (
            <>
              <Grid cols={3}>
                <MiniStat label="Payins collected" value={inrCompact(pinToday.v)} sub={`${pinToday.c} successful`} />
                <MiniStat label="Payouts sent" value={inrCompact(poToday.v)} sub={`${poToday.c} successful`} />
                <MiniStat label="Owed to merchants" value={inrCompact(gw.merchant_balances_total)} sub="sum of balances" />
              </Grid>
              <div style={{ marginTop: 14 }}>
                <Rows items={[
                  ["ProtonPay payout balance", gw.upstream_payout_balance != null ? inr(gw.upstream_payout_balance) : (gw.upstream_error || "—")],
                  ["Reconciliation", gap == null ? "—" : <Pill tone={gap >= 0 ? "green" : "red"} dot>{gap >= 0 ? "Covered" : "Short"} {inr(Math.abs(gap))}</Pill>],
                  ["Bank callbacks (last hour)", `${gw.callbacks_last_hour}${gw.unverified_callbacks_24h ? ` · ${gw.unverified_callbacks_24h} unverified in 24h` : ""}`],
                ]} />
              </div>
            </>
          )}
        </Section>

        <Section title="Payment attempts" sub="All modes, all time" icon={ArrowDownToLine}>
          <div className="ov-bar" aria-hidden="true">
            <i style={{ width: `${(intents.settled / totalIntents) * 100}%`, background: "#0f9d58" }} />
            <i style={{ width: `${(intents.awaiting / totalIntents) * 100}%`, background: "#2f33d6" }} />
            <i style={{ width: `${(intents.expired / totalIntents) * 100}%`, background: "#C9CEDB" }} />
          </div>
          <Rows items={[
            [<Legend c="#0f9d58" t="Settled" />, `${intents.settled.toLocaleString("en-IN")} · ${Math.round((intents.settled / totalIntents) * 100)}%`],
            [<Legend c="#2f33d6" t="Awaiting payment" />, intents.awaiting.toLocaleString("en-IN")],
            [<Legend c="#C9CEDB" t="Expired" />, intents.expired.toLocaleString("en-IN")],
          ]} />
        </Section>
      </div>

      <Grid cols={4}>
        <Kpi icon={Users} label="Users" value={ov.users} />
        <Kpi icon={Link2} label="Active payment links" value={ov.active_links.toLocaleString("en-IN")} />
        <Kpi icon={Smartphone} label="Capture devices" value={ov.devices} sub={outdated ? `${outdated} on an old app` : "all up to date"} onClick={() => go("devices")} />
        <Kpi icon={AlertTriangle} label="Unmatched credits" value={ov.unmatched_credits} sub="bank credits with no order" onClick={() => go("notifications")} />
      </Grid>

      <div className="k-split">
        <Section title="Mobile app in the field" sub={`Released version ${released || "—"}`} icon={Smartphone} pad={false}>
          <Table rowKey="version" rows={fleet} empty={<Empty icon={Smartphone} title="No devices yet">Devices appear once a merchant installs the capture app.</Empty>}
            columns={[
              { key: "version", label: "Version", render: (m) => <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}><span className="k-mono">{m.version}</span>{m.version === released ? <Pill tone="green">current</Pill> : <Pill tone="amber">update</Pill>}</span> },
              { key: "devices", label: "Devices", align: "right", render: (m) => m.devices.toLocaleString("en-IN") },
              { key: "last", label: "Last seen", align: "right", render: (m) => <Cell main={ago(m.last_seen)} /> },
            ]} />
        </Section>

        <Section title="System" sub="Refreshes every 10 seconds" icon={Server}>
          <Rows items={[
            ["Database", <Health ok={st.database.connected} label={st.database.connected ? "Connected" : "Down"} />],
            ["Background jobs", <Health ok={st.server.scheduler} label={st.server.scheduler ? "Running" : "Off"} />],
            ["Backend version", st.server.version, { mono: true }],
            ["Environment", <Pill tone={st.server.env === "production" ? "green" : "amber"}>{st.server.env}</Pill>],
            ["Uptime", uptime(st.server.uptime_seconds)],
          ]} />
        </Section>
      </div>

      <Section title="Database collections" sub={`${collections.length} collections`} icon={Database} pad={false}>
        <div className="ov-coll">
          {collections.map(([name, n]) => (
            <div key={name} className="ov-coll-row"><span className="k-mono">{name}</span><span>{n.toLocaleString("en-IN")}</span></div>
          ))}
        </div>
      </Section>

      <style>{`
        .ov-mini { background: #FAFBFD; border: 1px solid #EEF0F5; border-radius: 12px; padding: 12px 14px; min-width: 0; }
        .ov-mini span { display: block; font-size: 12.5px; color: #5b6170; }
        .ov-mini b { display: block; margin-top: 4px; font-size: 20px; font-weight: 700; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
        .ov-mini small { display: block; margin-top: 2px; font-size: 12px; color: #6b7280; }
        .ov-bar { display: flex; height: 10px; border-radius: 100px; overflow: hidden; background: #EEF0F5; margin-bottom: 10px; }
        .ov-bar i { height: 100%; }
        .ov-legend { display: inline-flex; align-items: center; gap: 8px; }
        .ov-legend i { width: 9px; height: 9px; border-radius: 3px; }
        .ov-coll { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .ov-coll-row { display: flex; justify-content: space-between; gap: 12px; padding: 10px 18px; border-bottom: 1px solid #F0F2F6; font-size: 13.5px; font-variant-numeric: tabular-nums; }
        .ov-coll-row span:first-child { color: #374151; overflow: hidden; text-overflow: ellipsis; }
        @media (max-width: 1000px) { .ov-coll { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 560px) { .ov-coll { grid-template-columns: minmax(0, 1fr); } }
      `}</style>
    </Page>
  );
}

function MiniStat({ label, value, sub }) {
  return <div className="ov-mini"><span>{label}</span><b>{value}</b>{sub && <small>{sub}</small>}</div>;
}

function Legend({ c, t }) {
  return <span className="ov-legend"><i style={{ background: c }} />{t}</span>;
}
